import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Select,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';
import useLocalStorage from '../../../hooks/useLocalStorage';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
import DIOText from '../../../components/DIOText';
import Image from '../../../components/Image';
// utils
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

const ScoutCoffeeChatProxyDialog = ({ profile, reload, proxyDone }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const [skipGuide, setSkipGuide] = useLocalStorage(`dioBizScoutProxyGuideDialog`, false);

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    roleName: '',
    salaryRange: '',
    title: '',
    stockOption: '',
    remoteWork: ''
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const getGroupValues = async () => {
    const companyId = user?.employee.company[0].id;
    try {
      setIsSubmit(true);
      const crewId = profile?.id;
      const response = await axios.get(`/api/company/${companyId}/fliers/${crewId}/coffee-group/`);
      setFormData({
        roleName: response?.data?.role_name || `${profile?.card_display_name || ''}` || `${profile?.main_role || ''} ${profile?.position_display || ''}` || '',
        salaryRange: response?.data?.salary_range || '',
        title: response?.data?.title || '',
        stockOption: response?.data?.stock_option || '',
        remoteWork: response?.data?.remote_work || ''
      });
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      if (error.response && error.response.status === 400) {
        enqueueSnackbar(error.response.data.message, { variant: 'error' });
      } else {
        enqueueSnackbar("데이터를 불러오는데 실패하였습니다. 관리자에 문의해주세요.", { variant: 'error' });
      }
    }
  };

  const handleNext = () => {
    setActiveStep(1);
    getGroupValues();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const crewId = profile?.id;
      await axios.post(`/api/company/${companyId}/scout/${crewId}/coffee-proxy/`, {
        offer_position: formData.roleName,
        salary_range: formData.salaryRange,
        title: formData.title,
        stock_option: formData.stockOption,
        remote_work: formData.remoteWork
      });
      reload();
      proxyDone();
      enqueueSnackbar("영업일 1일 이내로 커피챗 제안을 보내겠습니다.", { variant: 'success' });
      setIsSubmit(false);
      onClose();
    } catch (error) {
      setIsSubmit(false);
      enqueueSnackbar("데이터를 제출하는데 실패하였습니다. 관리자에 문의해주세요.", { variant: 'error' });
    }
  };

  useEffect(() => {
    if (open && skipGuide) {
      handleNext();
    } else {
      setActiveStep(0);
    }
  }, [open]);

  const onClose = () => {
    setOpen(false);
    setFormData({
      roleName: '',
      salaryRange: '',
      title: '',
      stockOption: '',
      remoteWork: '',
    });
  };

  const stopPropagationForTab = (e) => {
    if (e.key === "Tab" || e.key === "d" || e.key === "D") {
      // 최초 입력할때 d 를 누르면 [DIO에게 맡기기] 메뉴 아이템으로 인풋 포커스가 이동하는걸 방지하기 위해 추가함
      e.stopPropagation();
    }
  };

  return (
    <>
      <MenuItem
        onClick={() => setOpen(true)}
        disableRipple
        sx={{ m: 1, borderRadius: .5 }}
      >
        <SvgIconStyle src={`/icons/icon-lightning-mono.svg`} sx={{ mr: 2, width: 20, height: 20 }} />
        <Typography variant={"subtitle2"}><DIOText />에게 맡기기</Typography>
      </MenuItem>
      <Dialog open={open} onClose={onClose} maxWidth="dg" fullWidth onKeyDown={stopPropagationForTab}>
        {activeStep === 0 && (
          <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
            <Typography variant="h5" sx={{ mb: { xs: 2, sm: 3 } }}>
              <DIOText />가 커피챗 제안 메시지를{!smUp && <br />} 대신 보내드릴게요.
            </Typography>
            {smUp ? (
              <Image
                disabledEffect
                visibleByDefault
                alt="scout-proxy-modal"
                src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-proxy-modal.png"
                srcSet="
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-proxy-modal.png 1x,
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-proxy-modal-2x.png 2x,
                "
                sx={{
                  width: '100%',
                  mb: 2.5,
                }}
              />
            ) : (
              <Image
                disabledEffect
                visibleByDefault
                alt="scout-proxy-modal"
                src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-proxy-modal-mo.png"
                srcSet="
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-proxy-modal-mo.png 1x,
                  https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/scout/scout-proxy-modal-mo-2x.png 2x,
                "
                sx={{
                  width: '100%',
                  mb: 1.5,
                }}
              />
            )}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <SvgIconStyle
                src={`/icons/icon-check-circle-mono.svg`}
                sx={{ minWidth: smUp ? 20 : 16, minHeight: smUp ? 22 : 18, maxWidth: smUp ? 20 : 16, maxHeight: smUp ? 22 : 18, color: "info.main" }}
              />
              <Typography variant={smUp ? "body1" :"body2"} sx={{ wordBreak: "keep-all" }}>
                크루가 제안을 확인하지 못한 경우, 자동으로 리마인드 메시지도 보내드려요.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1}}>
              <SvgIconStyle
                src={`/icons/icon-arrow-right-circle-mono.svg`}
                sx={{
                  rotate: "270deg",
                  minWidth: smUp ? 20 : 16, minHeight: smUp ? 22 : 18, maxWidth: smUp ? 20 : 16, maxHeight: smUp ? 22 : 18, color: "info.main"
                }}
              />
              <Typography variant={smUp ? "body1" :"body2"} sx={{ wordBreak: "keep-all" }}>
                <DIOText />가 대신 제안하면, 크루의 커피챗 수락율이 최대 4배 높아져요!
              </Typography>
            </Stack>
          </DialogContent>
        )}
        {activeStep === 1 && (
          <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
            <>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="h5">
                  대략적인 연봉, 근무 조건도 함께 알려주세요.
                </Typography>
                <Typography variant="body2">
                  크루의 응답율을 높이기 위해 필수로 입력받고 있습니다.
                </Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  포지션
                </Typography>
                <TextField
                  fullWidth
                  name="roleName"
                  value={formData.roleName}
                  onChange={handleInputChange}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  연봉 수준
                </Typography>
                <TextField
                  fullWidth
                  name="salaryRange"
                  value={formData.salaryRange}
                  onChange={handleInputChange}
                  helperText={'예) 직전 연봉의 20% 인상 / 8천만원 ~ 1억 2천만원'}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  직급/직책
                </Typography>
                <TextField
                  fullWidth
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  helperText={'예) CTO, 테크리드, 백엔드 팀 리드, 시니어 개발자'}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body2">
                  <b>스톡옵션 조건</b> (선택)
                </Typography>
                <TextField
                  fullWidth
                  name="stockOption"
                  value={formData.stockOption}
                  onChange={handleInputChange}
                  helperText={'예) 1년 핏을 확인해본 후, n% 까지 지급할 의사가 있음'}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  재택근무 여부
                </Typography>
                <Select
                  fullWidth
                  displayEmpty
                  name="remoteWork"
                  value={formData.remoteWork}
                  onChange={handleInputChange}
                >
                  <MenuItem value={"전면 재택근무"} sx={{ m: 1, borderRadius: 1 }}>
                    전면 재택근무
                  </MenuItem>
                  <MenuItem value={"일부 재택근무 가능(상주+재택근무 혼합)"} sx={{ m: 1, borderRadius: 1 }}>
                    일부 재택근무 가능(상주+재택근무 혼합)
                  </MenuItem>
                  <MenuItem value={"재택근무 없음(전면 상주근무)"} sx={{ m: 1, borderRadius: 1 }}>
                    재택근무 없음(전면 상주근무)
                  </MenuItem>
                </Select>
                <Typography variant="caption" sx={{ pl: "14px", color: "grey.600" }}>
                  재택근무는 이직 제안시 어필 포인트가 될 수 있습니다.
                </Typography>
              </Stack>
            </Stack>
            </>
          </DialogContent>
        )}
        <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
          {activeStep === 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: 1, justifyContent: "space-between"}}>
              <FormGroup sx={{ mb: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={skipGuide} onClick={() => setSkipGuide((prev) => !prev)} />}
                  label="다시 보지 않기"
                />
              </FormGroup>
              <Button variant="contained" fullWidth={!smUp} size="large" sx={{ px: 4 }} onClick={handleNext}>
                확인했어요
              </Button>
            </Stack>
          )}
          {activeStep === 1 && (
            <>
              <LoadingButton
                variant="text"
                fullWidth={!smUp}
                size="large"
                sx={{ px: 4 }}
                onClick={onClose}
                loading={isSubmit}
              >
                취소
              </LoadingButton>
              <LoadingButton
                variant="contained"
                fullWidth={!smUp}
                size="large"
                sx={{ px: 4 }}
                onClick={handleSubmit}
                disabled={!formData.salaryRange || !formData.title || !formData.remoteWork}
                loading={isSubmit}
              >
                제출하기
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScoutCoffeeChatProxyDialog;
